import moment from 'moment';

/**
 * Represents a date and time.
 */
class DateTime {
  /**
   * Initializes a new instance of the DateTime class.
   * @param {Any} value The initial value of the date time.
   */
  constructor(value) {
    if (value?.toString() === 'DateTime') {
      this._moment = moment(value._moment);
    }
    else {
      this._moment = moment(value);
    }
  }

  /**
   * Adds time to the current date time.
   * @param {Number} amount The amount to add to the date time.
   * @param {String} unit The unit to add to (e.g. years, quarters, months, weeks, days, hours, minutes, seconds).
   * @returns {DateTime} The current instance, so that subsequent calls can be chained.
   */
  add(amount, unit) {
    this._moment.add(amount, unit);

    return this;
  }

  /**
   * 
   * @param {String} token The token used for formatting. 
   * @returns {String} The formatted date time.
   */
  format(token = String.empty) {
    return this._moment.format(token);
  }

  /**
   * Returns the current day of the month.
   * @returns {Number} The current day represented as a number.
   */
  getDate() {
    return this._moment.date();
  }

  /**
   * Returns the day of the week.
   * @returns {Number} The day of the week represented as a number.
   */
  getDay() {
    return this._moment.day();
  }

  /**
   * Returns the days in the current month.
   * @returns {Array<Number>} The days in the month.
   */
  getDaysInMonth() {
    return this._moment.daysInMonth();
  }

  /**
   * Returns the current month of the year.
   * @returns {Number} The current month of the year.
   */
  getMonth() {
    return this._moment.month();
  }

  /**
   * Returns all of the months.
   * @returns {Array<String>} The months.
   */
  static getMonths() {
    const months = [];
    const dateTime = new DateTime();
    const currYear = dateTime.getYear();
    const startDateTime = new DateTime(`${currYear}-01-01T00:00:00.000`);

    for (let index = 0; index < 12; ++index) {
      const month = startDateTime.format('MMMM');

      months.push(month);

      startDateTime.add(1, 'months');
    }

    return months;
  }

  /**
   * Returns the current year
   * @returns {Number} The current year.
   */
  getYear() {
    return this._moment.year();
  }

  /**
   * Returns the current date time.
   * @returns {Date} The current date time.
   */
  static now() {
    return moment().toDate();
  }

  /**
   * Sets the current date.
   * @param {Number} date The date to set.
   * @returns {DateTime} The current instance.
   */
  setDate(date) {
    this._moment.date(date);

    return this;
  }

  /**
   * Sets the hour of the date time.
   * @param {Number} hour The hour to set.
   * @returns {DateTime} The current instance.
   */
  setHour(hour) {
    this._moment.hour(hour);

    return this;
  }

  /**
   * Sets the minute of the date time.
   * @param {Number} minute The minute to set.
   * @returns {DateTime} The current instance.
   */
  setMinute(minute) {
    this._moment.minute(minute);

    return this;
  }

  /**
   * Sets the month of the date time.
   * @param {Number} month The month to set.
   * @returns {DateTime} The current instance.
   */
  setMonth(month) {
    this._moment.set('month', month);

    return this;
  }

  /**
   * Sets the current year of the date time.
   * @param {Number} year The year to set.
   * @returns {DateTime} The current instance.
   */
  setYear(year) {
    this._moment.year(year);

    return this;
  }

  /**
   * Sets the date time to the start of the provided unit time.
   * @param {String} unit The unit to set.
   * @returns {DateTime} The current instance.
   */
  startOf(unit) {
    this._moment.startOf(unit);

    return this;
  }

  /**
   * Subtracts time from the current date time.
   * @param {Number} amount The amount to subtract from the date time.
   * @param {String} unit The unit to subtract from (e.g. years, quarters, months, weeks, days, hours, minutes, seconds).
   * @returns {DateTime} The current instance.
   */
  subtract(amount, unit) {
    this._moment.subtract(amount, unit);

    return this;
  }

  /**
   * Returns a Date as UTC.
   * @returns {Date} The current date time as UTC.
   */
  toUtc() {
    return this._moment.utc().toDate();
  }

  /**
   * Returns a string representing the current date time in UTC.
   * @returns {String} The current date time as a UTC formatted string.
   */
  toUtcString() {
    return this._moment.utc().format();
  }

  /**
   * Returns the days of the week.
   * @returns {Array<String>} The array of week day strings.
   */
  static weekdays() {
    return moment.weekdays();
  }

  /**
   * Retruns the short days of the week.
   * @returns {Array<String>} The array of short week day strings.
   */
  static weekdaysShort() {
    return moment.weekdaysShort();
  }
}

/**
 * Returns a string representing the type.
 * @returns {String} A string representing the type.
 */
DateTime.prototype.toString = function() {
  return 'DateTime';
};

export default DateTime;
